import {
  inputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { columnRateKind } from "@/lib/manufacturing/cogsColumnEdit";
import type { ManufacturingPricingGridState } from "./useManufacturingPricingGrid";
import { ManufacturingPricingColumnsPanel } from "./ManufacturingPricingColumnsPanel";

type Props = {
  grid: ManufacturingPricingGridState;
};

function opHint(column: ManufacturingPricingGridState["columns"][number]): string {
  if (column.op === "margin_denom") return "Margin fraction (0.15 = 15% of the final price)";
  if (column.op === "multiply" && columnRateKind(column.metadata) === "markup") {
    return "Markup fraction (0.15 = 15% added on top)";
  }
  if (column.op === "multiply") return "Factor (e.g. 1.1)";
  return "USD amount";
}

export function ManufacturingPricingBulkEditBody({ grid }: Props) {
  const g = grid;
  const selectedCol = g.columns.find((c) => c.id === g.bulkColumnId);
  return (
    <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:items-end">
      <label className="block space-y-1 text-sm">
        <span className="text-gray-600 dark:text-zinc-400">Layer column</span>
        <select
          className={inputClass}
          value={g.bulkColumnId}
          onChange={(e) => g.setBulkColumnId(e.target.value)}
          disabled={g.actionBusy}
        >
          {g.columns
            .filter((c) => c.is_active)
            .map((c) => (
              <option key={c.id} value={c.id}>
                [{c.band}] {c.label}
              </option>
            ))}
        </select>
      </label>
      <label className="block space-y-1 text-sm">
        <span className="text-gray-600 dark:text-zinc-400">
          {selectedCol ? opHint(selectedCol) : "Value"}
        </span>
        <input
          className={inputClass}
          type="number"
          step="any"
          value={g.bulkValue}
          onChange={(e) => g.setBulkValue(e.target.value)}
          disabled={g.actionBusy}
        />
      </label>
      <button
        type="button"
        className={mfgPrimaryBtnClass}
        onClick={() => void g.applyBulkValue()}
        disabled={g.actionBusy || g.selectedCount === 0 || !g.bulkColumnId}
      >
        {g.actionBusy ? "Applying…" : `Set on ${g.selectedCount} selected`}
      </button>
    </div>
  );
}

export function ManufacturingPricingColumnsBody({ grid }: Props) {
  return <ManufacturingPricingColumnsPanel grid={grid} />;
}

/** @deprecated Prefer tools card tabs; kept for any stray imports. */
export function ManufacturingPricingBulkMarginsCard({ grid }: Props) {
  return (
    <div className="space-y-4">
      <ManufacturingPricingBulkEditBody grid={grid} />
      <div className="border-t border-gray-200 pt-4 dark:border-zinc-800">
        <ManufacturingPricingColumnsBody grid={grid} />
      </div>
    </div>
  );
}
